import { useState } from "react";
import { api, API } from "@/lib/api";
import AiChatPanel from "@/components/AiChatPanel";
import ScriptViewer from "@/components/ScriptViewer";
import ProjectDocuments from "@/components/ProjectDocuments";

/** Script tab with 3 formats: treatment / scaleta / screenplay. */
export default function ScriptTab({ project, onChange, onSave, onSaveProject }) {
  const script = project.script || { format: "treatment", treatment_blocks: [], scenes: [], notes: "" };
  const setScript = (s) => onChange({ ...project, script: s });

  const setFormat = (f) => setScript({ ...script, format: f });
  const onBlur = () => onSave?.();
  const [importOpen, setImportOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [chatTarget, setChatTarget] = useState(null);   // { label, value, onApply }
  const [deriveMsg, setDeriveMsg] = useState("");
  const [deriving, setDeriving] = useState(null);       // 'scaleta' | 'screenplay' | null
  const [genFromScript, setGenFromScript] = useState(null);  // 'treatment' | 'scaleta' | 'screenplay' | null
  const [genError, setGenError] = useState("");

  // Polling helper for async AI jobs
  const pollJob = async (jobId, maxMs = 8 * 60 * 1000) => {
    const startedAt = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, 4000));
      const p = await api.get(`/icaa/ai/jobs/${jobId}`);
      if (p.data.status === "done") return p.data.result;
      if (p.data.status === "error") {
        const err = p.data.error || "Error desconocido";
        if (err.toLowerCase().includes("budget")) {
          throw new Error("Se ha agotado el saldo de la IA. Pide a Eduardo que recargue en Profile → Universal Key → Add Balance.");
        }
        throw new Error(err);
      }
      if (Date.now() - startedAt > maxMs) throw new Error("La IA tardó demasiado.");
    }
  };

  // Generate treatment/scaleta/screenplay directly from the uploaded script
  const generateFromScript = async (targetFormat) => {
    if (!project.script?.uploaded_script?.filename) return;
    setGenFromScript(targetFormat); setGenError("");
    try {
      const r = await api.post(`/icaa/projects/${project.id}/ai/script/distribute`,
        { use_uploaded: true, target_format: targetFormat });
      const result = await pollJob(r.data.job_id);
      const blocks = result?.blocks?.length || 0;
      const scenes = result?.scenes?.length || 0;
      if (blocks === 0 && scenes === 0) {
        throw new Error("La IA respondió sin contenido. Inténtalo de nuevo o verifica que el guion subido tenga texto suficiente.");
      }
      // applyImport already sets the right format internally; don't call setFormat
      // after it (would overwrite the new blocks/scenes with stale state)
      applyImport(result, "replace", targetFormat);
      const n = blocks || scenes;
      const unit = blocks ? (blocks === 1 ? "bloque" : "bloques") : (scenes === 1 ? "escena" : "escenas");
      setDeriveMsg(`✓ ${targetFormat === "treatment" ? "Tratamiento" : targetFormat === "scaleta" ? "Escaleta" : "Guion técnico"} generado: ${n} ${unit}.`);
      setTimeout(() => setDeriveMsg(""), 5000);
    } catch (e) {
      setGenError(e?.response?.data?.detail || e.message);
    } finally { setGenFromScript(null); }
  };

  // ---- treatment blocks
  const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const renumber = (arr) => arr.map((x, k) => ({ ...x, number: k + 1 }));

  const addBlock = () => setScript({
    ...script,
    treatment_blocks: renumber([
      ...(script.treatment_blocks || []),
      { id: newId(), title: "Nuevo bloque", period: "", summary: "", body: "", sequences: [] },
    ]),
  });
  const updBlock = (i, patch) => setScript({
    ...script,
    treatment_blocks: script.treatment_blocks.map((b, j) => j === i ? { ...b, ...patch } : b),
  });
  const delBlock = (i) => setScript({
    ...script,
    treatment_blocks: renumber(script.treatment_blocks.filter((_, j) => j !== i)),
  });
  const moveBlock = (i, dir) => {
    const arr = [...script.treatment_blocks];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setScript({ ...script, treatment_blocks: renumber(arr) });
  };

  // ---- sequences inside a treatment block
  const addSeq = (bi) => updBlock(bi, {
    sequences: renumber([
      ...((script.treatment_blocks[bi]?.sequences) || []),
      { id: newId(), title: "Nueva secuencia", location: "", summary: "", body: "" },
    ]),
  });
  const updSeq = (bi, si, patch) => {
    const seqs = (script.treatment_blocks[bi].sequences || []).map((s, j) => j === si ? { ...s, ...patch } : s);
    updBlock(bi, { sequences: seqs });
  };
  const delSeq = (bi, si) => {
    const seqs = renumber((script.treatment_blocks[bi].sequences || []).filter((_, j) => j !== si));
    updBlock(bi, { sequences: seqs });
  };
  const moveSeq = (bi, si, dir) => {
    const arr = [...(script.treatment_blocks[bi].sequences || [])];
    const j = si + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[si], arr[j]] = [arr[j], arr[si]];
    updBlock(bi, { sequences: renumber(arr) });
  };

  // ---- scenes (scaleta + screenplay)
  const addScene = () => setScript({
    ...script,
    scenes: renumber([
      ...(script.scenes || []),
      { id: newId(), title: "Nueva escena",
        location: "INT./EXT. LUGAR", time: "DÍA", duration_min: 0, summary: "", body: "" },
    ]),
  });
  const updScene = (i, patch) => setScript({
    ...script,
    scenes: script.scenes.map((s, j) => j === i ? { ...s, ...patch } : s),
  });
  const delScene = (i) => setScript({
    ...script,
    scenes: renumber(script.scenes.filter((_, j) => j !== i)),
  });

  // ---- derive from treatment (treatment -> scaleta/screenplay)
  const derive = async (target, mode) => {
    setDeriving(target);
    setDeriveMsg("");
    try {
      const r = await api.post(`/icaa/projects/${project.id}/ai/script/derive`, { target_format: target }, { timeout: 240000 });
      const incoming = (r.data.scenes || []).map((s, k) => ({
        id: newId(), number: k + 1,
        title: s.title || "",
        location: s.location || "INT./EXT. LUGAR",
        time: s.time || "DÍA",
        duration_min: Number(s.duration_min) || 0,
        summary: s.summary || "", body: s.body || "",
      }));
      const merged = mode === "replace" ? incoming : [...(script.scenes || []), ...incoming];
      setScript({ ...script, format: target, scenes: merged.map((s, k) => ({ ...s, number: k + 1 })) });
      setDeriveMsg(`✓ ${incoming.length} escenas generadas en ${target === "scaleta" ? "escaleta" : "guion técnico"}.`);
      setTimeout(() => onSave?.(), 200);
    } catch (e) {
      setDeriveMsg("Error: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setDeriving(null);
    }
  };

  // ---- AI import handler
  const applyImport = (data, mode, requestedFormat) => {
    let newScript = script;
    if (data.blocks) {
      const incoming = data.blocks.map((b, k) => ({
        id: newId(),
        number: k + 1,
        title: b.title || `Bloque ${k + 1}`,
        period: b.period || "",
        summary: b.summary || "",
        body: b.body || "",
        sequences: (b.sequences || []).map((s, j) => ({
          id: newId(), number: j + 1,
          title: s.title || "", location: s.location || "",
          summary: s.summary || "", body: s.body || "",
        })),
      }));
      const merged = mode === "replace" ? incoming : [...(script.treatment_blocks || []), ...incoming];
      newScript = { ...script, format: "treatment", treatment_blocks: merged.map((b, k) => ({ ...b, number: k + 1 })) };
    } else if (data.scenes) {
      const incoming = data.scenes.map((s, k) => ({
        id: newId(), number: k + 1,
        title: s.title || "",
        location: s.location || "INT./EXT. LUGAR",
        time: s.time || "DÍA",
        duration_min: Number(s.duration_min) || 0,
        summary: s.summary || "", body: s.body || "",
      }));
      const merged = mode === "replace" ? incoming : [...(script.scenes || []), ...incoming];
      const fmt = requestedFormat === "screenplay" ? "screenplay" : "scaleta";
      newScript = { ...script, format: fmt, scenes: merged.map((s, k) => ({ ...s, number: k + 1 })) };
    } else {
      setImportOpen(false);
      return;
    }
    const updatedProject = { ...project, script: newScript };
    onChange(updatedProject);
    setImportOpen(false);
    // Save directly with the fresh project (avoid stale closure of onSave)
    if (onSaveProject) onSaveProject(updatedProject);
    else setTimeout(() => onSave?.(), 100);
  };

  return (
    <section className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-8" data-testid="script-section">
      {/* Format selector */}
      <div className="mb-4 flex items-center gap-1 overflow-x-auto rounded-full border border-stone-300/70 bg-white p-1">
        <Pill label="Tratamiento literario" active={script.format === "treatment"} onClick={() => setFormat("treatment")} testid="script-fmt-treatment" />
        <Pill label="Escaleta" active={script.format === "scaleta"} onClick={() => setFormat("scaleta")} testid="script-fmt-scaleta" />
        <Pill label="Guion técnico" active={script.format === "screenplay"} onClick={() => setFormat("screenplay")} testid="script-fmt-screenplay" />
        <span className="ml-auto hidden px-3 text-xs text-stone-500 lg:inline">{
          script.format === "treatment" ? "Bloques narrativos con secuencias dentro"
          : script.format === "scaleta" ? "Lista de escenas con duración"
          : "INT./EXT. — escena/acción/diálogo"
        }</span>
      </div>

      {/* Uploaded script panel: rich card with all the actions */}
      {script.uploaded_script?.filename ? (
        <div className="mb-5 overflow-hidden rounded-2xl border border-[#26775E]/30 bg-white shadow-sm" data-testid="uploaded-script-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 bg-gradient-to-br from-[#26775E]/8 to-transparent px-5 py-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#26775E]">📄 Guion subido</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-stone-900">{script.uploaded_script.filename}</p>
              <p className="text-[11px] text-stone-500">{(script.uploaded_script.char_count || 0).toLocaleString()} caracteres</p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => setViewerOpen(true)} data-testid="script-view-btn"
                      className="rounded-full bg-stone-900 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#26775E]">
                👁 Ver
              </button>
              <label className="cursor-pointer rounded-full bg-stone-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-stone-700 hover:bg-stone-300"
                     data-testid="script-replace-btn">
                <input type="file" accept=".pdf,.txt,.fountain,.fdx" hidden data-testid="script-replace-input"
                       onChange={async (e) => {
                         const file = e.target.files?.[0]; if (!file) return;
                         setUploading(true); setUploadMsg("");
                         try {
                           const fd = new FormData(); fd.append("file", file);
                           const r = await api.post(`/icaa/projects/${project.id}/script/upload`, fd, {
                             headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
                           });
                           setUploadMsg(`✓ ${r.data.filename} subido (${r.data.char_count.toLocaleString()} caracteres).`);
                           onChange({ ...project, script: { ...(project.script || {}), uploaded_script: {
                             filename: r.data.filename, char_count: r.data.char_count,
                             uploaded_at: new Date().toISOString(), text: "...",
                           }}});
                         } catch (err) {
                           setUploadMsg("Error: " + (err?.response?.data?.detail || err.message));
                         } finally { setUploading(false); e.target.value = ""; }
                       }} />
                {uploading ? "Subiendo…" : "↻ Reemplazar"}
              </label>
              <button onClick={async () => {
                if (!window.confirm("¿Eliminar el guion subido? Esto no borra los bloques o escenas que ya hayas generado.")) return;
                try {
                  await api.delete(`/icaa/projects/${project.id}/script/upload`);
                  onChange({ ...project, script: { ...(project.script || {}), uploaded_script: null } });
                  setUploadMsg("");
                } catch (e) { alert(e?.response?.data?.detail || e.message); }
              }} data-testid="script-delete-btn"
                      className="rounded-full bg-stone-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-stone-700 hover:bg-red-100 hover:text-red-700">
                🗑
              </button>
            </div>
          </div>
          <div className="px-5 py-4">
            <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500">Crear automáticamente desde este guion:</p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <GenButton label="Tratamiento" subtitle="Bloques narrativos + secuencias"
                         busy={genFromScript === "treatment"} disabled={!!genFromScript}
                         onClick={() => generateFromScript("treatment")}
                         testid="gen-treatment-from-script" />
              <GenButton label="Escaleta" subtitle="Lista de escenas con duración"
                         busy={genFromScript === "scaleta"} disabled={!!genFromScript}
                         onClick={() => generateFromScript("scaleta")}
                         testid="gen-scaleta-from-script" />
              <GenButton label="Guion técnico" subtitle="INT./EXT. — escena/acción/diálogo"
                         busy={genFromScript === "screenplay"} disabled={!!genFromScript}
                         onClick={() => generateFromScript("screenplay")}
                         testid="gen-screenplay-from-script" />
            </div>
            {genFromScript && (
              <p className="mt-3 rounded-md bg-[#26775E]/8 px-3 py-2 text-[12px] text-[#26775E]" data-testid="gen-progress">
                ⏳ Generando con IA… puede tardar 1-2 minutos. No cierres esta pestaña.
              </p>
            )}
            {genError && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="gen-error">{genError}</p>}
            {deriveMsg && <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="derive-msg-uploaded">{deriveMsg}</p>}
          </div>
        </div>
      ) : (
        <div className="mb-5 rounded-2xl border-2 border-dashed border-stone-300 bg-white p-5" data-testid="upload-empty-panel">
          <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500">¿Tienes guion?</p>
          <h3 className="mt-1 font-display text-xl text-stone-900">Empieza subiendo tu guion o pegando un texto</h3>
          <p className="mt-1 text-[13px] text-stone-600">
            Si ya tienes un guion (PDF, TXT, Fountain), súbelo y la IA podrá generar tratamiento, escaleta o guion técnico con un click.
            Si solo tienes un texto narrativo, pégalo y la IA lo distribuirá en bloques o escenas.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <label className="cursor-pointer rounded-full bg-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#1c5c48]"
                   data-testid="script-upload-btn">
              <input type="file" accept=".pdf,.txt,.fountain,.fdx" hidden data-testid="script-upload-input"
                     onChange={async (e) => {
                       const file = e.target.files?.[0]; if (!file) return;
                       setUploading(true); setUploadMsg("");
                       try {
                         const fd = new FormData(); fd.append("file", file);
                         const r = await api.post(`/icaa/projects/${project.id}/script/upload`, fd, {
                           headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
                         });
                         setUploadMsg(`✓ ${r.data.filename} subido (${r.data.char_count.toLocaleString()} caracteres).`);
                         onChange({ ...project, script: { ...(project.script || {}), uploaded_script: {
                           filename: r.data.filename, char_count: r.data.char_count,
                           uploaded_at: new Date().toISOString(), text: "...",
                         }}});
                       } catch (err) {
                         setUploadMsg("Error: " + (err?.response?.data?.detail || err.message));
                       } finally { setUploading(false); e.target.value = ""; }
                     }} />
              {uploading ? "Subiendo…" : "📥 Subir guion (PDF)"}
            </label>
            <button onClick={() => setImportOpen(true)} data-testid="script-import-btn"
                    className="rounded-full border border-[#26775E] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-[#26775E] hover:bg-[#26775E] hover:text-white">
              📝 Pegar texto y distribuir
            </button>
          </div>
        </div>
      )}

      {uploadMsg && (
        <p className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="script-upload-msg">{uploadMsg}</p>
      )}

      {script.format === "treatment" && (script.treatment_blocks || []).length > 0 && (
        <div className="mb-5 rounded-xl border border-[#26775E]/30 bg-[#26775E]/5 p-3 sm:p-4" data-testid="derive-bar">
          <div className="flex flex-wrap items-center gap-2">
            <p className="mr-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#26775E]">Derivar a otro formato:</p>
            <button onClick={() => derive("scaleta", "replace")} disabled={!!deriving} data-testid="derive-scaleta-btn"
                    className="rounded-full bg-stone-900 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#26775E] disabled:opacity-40">
              {deriving === "scaleta" ? "Generando…" : "Generar escaleta"}
            </button>
            <button onClick={() => derive("screenplay", "replace")} disabled={!!deriving} data-testid="derive-screenplay-btn"
                    className="rounded-full bg-stone-900 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#26775E] disabled:opacity-40">
              {deriving === "screenplay" ? "Generando…" : "Generar guion técnico"}
            </button>
            <span className="text-[11px] italic text-stone-500">Reemplaza el formato destino con escenas derivadas de tus bloques + secuencias.</span>
          </div>
          {deriveMsg && <p className="mt-2 text-xs text-stone-700" data-testid="derive-msg">{deriveMsg}</p>}
        </div>
      )}

      <textarea value={script.notes || ""} onChange={(e) => setScript({ ...script, notes: e.target.value })}
                onBlur={onBlur} data-testid="script-notes"
                rows={2} placeholder="Notas generales del guion / tratamiento (visibles solo para el equipo)."
                className="mb-6 w-full resize-y rounded-md border border-stone-200 bg-[#FBFAF4] p-3 text-sm outline-none focus:border-[#26775E]" />

      {script.format === "treatment" && (
        <>
          {(script.treatment_blocks || []).map((b, i) => (
            <article key={b.id} className="mb-4 overflow-hidden rounded-xl border border-stone-300/70 bg-white" data-testid={`block-${i}`}>
              <header className="flex items-center gap-3 bg-stone-900 px-5 py-3 text-[#FAFAF6]">
                <span className="font-mono text-xs text-[#26775E]">{String(i + 1).padStart(2, "0")}</span>
                <input value={b.title} onChange={(e) => updBlock(i, { title: e.target.value })} onBlur={onBlur}
                       data-testid={`block-title-${i}`}
                       className="flex-1 bg-transparent font-serif text-base outline-none" />
                <input value={b.period} onChange={(e) => updBlock(i, { period: e.target.value })} onBlur={onBlur}
                       placeholder="Periodo / fecha" data-testid={`block-period-${i}`}
                       className="w-40 bg-transparent text-right text-xs italic text-stone-300 outline-none" />
                <button onClick={() => moveBlock(i, -1)} className="rounded-full px-2 text-stone-400 hover:text-white">↑</button>
                <button onClick={() => moveBlock(i, 1)} className="rounded-full px-2 text-stone-400 hover:text-white">↓</button>
                <button onClick={() => delBlock(i)} data-testid={`block-del-${i}`}
                        className="rounded-full px-2 text-stone-400 hover:text-red-300">×</button>
              </header>
              <div className="space-y-3 p-5">
                <input value={b.summary} onChange={(e) => updBlock(i, { summary: e.target.value })} onBlur={onBlur}
                       placeholder="Sinopsis breve del bloque (1 línea)" data-testid={`block-summary-${i}`}
                       className="w-full rounded border border-stone-200 bg-[#FBFAF4] px-3 py-2 text-sm italic outline-none focus:border-[#26775E]" />
                <div className="relative">
                  <textarea value={b.body} onChange={(e) => updBlock(i, { body: e.target.value })} onBlur={onBlur}
                            rows={5} placeholder="Desarrollo del bloque: localizaciones, acciones, personajes, recursos." data-testid={`block-body-${i}`}
                            className="w-full resize-y rounded border border-stone-200 bg-[#FBFAF4] p-3 pr-24 font-serif text-[14px] leading-relaxed outline-none focus:border-[#26775E]" />
                  <button onClick={() => setChatTarget({
                            label: `Bloque ${i + 1}: ${b.title || ""}`,
                            value: b.body || "",
                            onApply: (text, mode) => {
                              const next = mode === "append" ? (b.body ? b.body + "\n\n" + text : text) : text;
                              updBlock(i, { body: next });
                              setTimeout(() => onSave?.(), 200);
                            },
                          })}
                          data-testid={`block-chat-${i}`}
                          className="absolute right-2 top-2 rounded-full border border-[#26775E] bg-white px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#26775E] hover:bg-[#26775E] hover:text-white">
                    💬 IA
                  </button>
                </div>

                {/* Sequences inside the block */}
                <div className="mt-2 rounded-lg border border-stone-200 bg-[#FAFAF6]/60 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#26775E]">
                      Secuencias del bloque ({(b.sequences || []).length})
                    </p>
                    <button onClick={() => addSeq(i)} data-testid={`seq-add-${i}`}
                            className="rounded-full border border-[#26775E]/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#26775E] hover:bg-[#26775E] hover:text-white">
                      + Secuencia
                    </button>
                  </div>
                  {(b.sequences || []).length === 0 && (
                    <p className="py-3 text-center text-xs italic text-stone-400">Aún no hay secuencias en este bloque.</p>
                  )}
                  {(b.sequences || []).map((sq, si) => (
                    <div key={sq.id} className="mb-2 rounded-md border border-stone-200 bg-white p-3" data-testid={`seq-${i}-${si}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#26775E]">{String(i + 1).padStart(2,"0")}.{String(si+1).padStart(2,"0")}</span>
                        <input value={sq.title} onChange={(e) => updSeq(i, si, { title: e.target.value })} onBlur={onBlur}
                               placeholder="Título de la secuencia" data-testid={`seq-title-${i}-${si}`}
                               className="flex-1 bg-transparent text-sm font-medium outline-none focus:border-b focus:border-[#26775E]" />
                        <input value={sq.location} onChange={(e) => updSeq(i, si, { location: e.target.value })} onBlur={onBlur}
                               placeholder="Localización" data-testid={`seq-loc-${i}-${si}`}
                               className="w-44 bg-transparent text-right font-mono text-[11px] uppercase text-stone-500 outline-none" />
                        <button onClick={() => moveSeq(i, si, -1)} className="px-1 text-stone-400 hover:text-[#26775E]">↑</button>
                        <button onClick={() => moveSeq(i, si, 1)} className="px-1 text-stone-400 hover:text-[#26775E]">↓</button>
                        <button onClick={() => delSeq(i, si)} data-testid={`seq-del-${i}-${si}`}
                                className="px-1 text-stone-400 hover:text-red-500">×</button>
                      </div>
                      <input value={sq.summary} onChange={(e) => updSeq(i, si, { summary: e.target.value })} onBlur={onBlur}
                             placeholder="Sinopsis breve (1 línea)" data-testid={`seq-summary-${i}-${si}`}
                             className="mt-2 w-full bg-transparent text-xs italic text-stone-600 outline-none placeholder:text-stone-300" />
                      <div className="relative">
                        <textarea value={sq.body} onChange={(e) => updSeq(i, si, { body: e.target.value })} onBlur={onBlur}
                                  rows={3} placeholder="Desarrollo de la secuencia." data-testid={`seq-body-${i}-${si}`}
                                  className="mt-2 w-full resize-y rounded border border-stone-100 bg-[#FBFAF4] p-2 pr-20 font-serif text-[13px] leading-relaxed outline-none focus:border-[#26775E]" />
                        <button onClick={() => setChatTarget({
                                  label: `Secuencia ${i + 1}.${si + 1}: ${sq.title || ""}`,
                                  value: sq.body || "",
                                  onApply: (text, mode) => {
                                    const next = mode === "append" ? (sq.body ? sq.body + "\n\n" + text : text) : text;
                                    updSeq(i, si, { body: next });
                                    setTimeout(() => onSave?.(), 200);
                                  },
                                })}
                                data-testid={`seq-chat-${i}-${si}`}
                                className="absolute right-1.5 top-3 rounded-full border border-[#26775E] bg-white px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#26775E] hover:bg-[#26775E] hover:text-white">
                          💬 IA
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
          <button onClick={addBlock} data-testid="block-add"
                  className="w-full rounded-xl border-2 border-dashed border-stone-300 bg-white/60 py-6 text-sm uppercase tracking-widest text-stone-500 hover:border-[#26775E] hover:text-[#26775E]">
            + Añadir bloque
          </button>
        </>
      )}

      {script.format === "scaleta" && (
        <>
          <div className="overflow-x-auto rounded-xl border border-stone-300/70 bg-white">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="bg-stone-900 text-xs uppercase tracking-widest text-[#FAFAF6]">
                  <th className="w-12 px-3 py-3 text-left">#</th>
                  <th className="px-3 py-3 text-left">Título / acción</th>
                  <th className="w-44 px-3 py-3 text-left">Localización</th>
                  <th className="w-28 px-3 py-3 text-left">Día/Noche</th>
                  <th className="w-24 px-3 py-3 text-right">Min</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(script.scenes || []).map((s, i) => (
                  <tr key={s.id} className="border-t border-stone-100" data-testid={`scene-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs text-stone-500">{i + 1}</td>
                    <td className="px-3 py-2"><input value={s.title} onChange={(e) => updScene(i, { title: e.target.value })} onBlur={onBlur}
                                                       data-testid={`scene-title-${i}`}
                                                       className="w-full bg-transparent outline-none" /></td>
                    <td className="px-3 py-2"><input value={s.location} onChange={(e) => updScene(i, { location: e.target.value })} onBlur={onBlur}
                                                       data-testid={`scene-loc-${i}`}
                                                       className="w-full bg-transparent font-mono text-xs uppercase outline-none" /></td>
                    <td className="px-3 py-2"><select value={s.time} onChange={(e) => updScene(i, { time: e.target.value })} onBlur={onBlur}
                                                       data-testid={`scene-time-${i}`}
                                                       className="bg-transparent text-xs outline-none">
                      {["DÍA","NOCHE","ATARDECER","AMANECER"].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></td>
                    <td className="px-3 py-2 text-right"><input type="number" step="0.5" value={s.duration_min}
                                                                  onChange={(e) => updScene(i, { duration_min: Number(e.target.value) })} onBlur={onBlur}
                                                                  data-testid={`scene-dur-${i}`}
                                                                  className="w-20 bg-transparent text-right outline-none" /></td>
                    <td className="px-2"><button onClick={() => delScene(i)} data-testid={`scene-del-${i}`}
                                                  className="text-stone-400 hover:text-red-600">×</button></td>
                  </tr>
                ))}
                {!(script.scenes || []).length && (
                  <tr><td colSpan={6} className="py-10 text-center text-stone-400">Aún no hay escenas.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-[#26775E] text-white">
                  <td colSpan={4} className="px-3 py-2 uppercase tracking-widest text-xs">Total</td>
                  <td className="px-3 py-2 text-right">
                    {(script.scenes || []).reduce((s, sc) => s + Number(sc.duration_min || 0), 0)} min
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <button onClick={addScene} data-testid="scene-add"
                  className="mt-3 w-full rounded-xl border-2 border-dashed border-stone-300 bg-white/60 py-4 text-sm uppercase tracking-widest text-stone-500 hover:border-[#26775E] hover:text-[#26775E]">
            + Añadir escena
          </button>
        </>
      )}

      {script.format === "screenplay" && (
        <>
          {(script.scenes || []).map((s, i) => (
            <article key={s.id} className="mb-4 overflow-hidden rounded-xl border border-stone-300/70 bg-white" data-testid={`scn-${i}`}>
              <header className="flex items-center gap-2 bg-stone-900 px-4 py-2 text-[#FAFAF6]">
                <span className="font-mono text-xs text-[#26775E]">{i + 1}.</span>
                <input value={s.location} onChange={(e) => updScene(i, { location: e.target.value })} onBlur={onBlur}
                       data-testid={`scn-loc-${i}`}
                       className="flex-1 bg-transparent font-mono text-xs uppercase outline-none" placeholder="INT./EXT. LUGAR" />
                <select value={s.time} onChange={(e) => updScene(i, { time: e.target.value })} onBlur={onBlur}
                        data-testid={`scn-time-${i}`}
                        className="bg-transparent font-mono text-xs uppercase outline-none">
                  {["DÍA","NOCHE","ATARDECER","AMANECER"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => delScene(i)} data-testid={`scn-del-${i}`}
                        className="text-stone-400 hover:text-red-300">×</button>
              </header>
              <div className="space-y-2 p-4">
                <input value={s.title} onChange={(e) => updScene(i, { title: e.target.value })} onBlur={onBlur}
                       data-testid={`scn-title-${i}`}
                       placeholder="Título de escena (opcional)"
                       className="w-full bg-transparent font-serif text-base outline-none focus:border-b focus:border-[#26775E]" />
                <div className="relative">
                  <textarea value={s.body} onChange={(e) => updScene(i, { body: e.target.value })} onBlur={onBlur}
                            rows={6} data-testid={`scn-body-${i}`}
                            placeholder="ACCIÓN.&#10;&#10;PERSONAJE&#10;Diálogo."
                            className="w-full resize-y rounded border border-stone-200 bg-[#FBFAF4] p-3 pr-20 font-mono text-[13px] leading-relaxed outline-none focus:border-[#26775E]" />
                  <button onClick={() => setChatTarget({
                            label: `Escena ${i + 1}: ${s.title || s.location || ""}`,
                            value: s.body || "",
                            onApply: (text, mode) => {
                              const next = mode === "append" ? (s.body ? s.body + "\n\n" + text : text) : text;
                              updScene(i, { body: next });
                              setTimeout(() => onSave?.(), 200);
                            },
                          })}
                          data-testid={`scn-chat-${i}`}
                          className="absolute right-2 top-2 rounded-full border border-[#26775E] bg-white px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#26775E] hover:bg-[#26775E] hover:text-white">
                    💬 IA
                  </button>
                </div>
              </div>
            </article>
          ))}
          <button onClick={addScene} data-testid="scn-add"
                  className="w-full rounded-xl border-2 border-dashed border-stone-300 bg-white/60 py-6 text-sm uppercase tracking-widest text-stone-500 hover:border-[#26775E] hover:text-[#26775E]">
            + Añadir escena
          </button>
        </>
      )}

      {importOpen && (
        <ImportModal
          projectId={project.id}
          defaultFormat={script.format}
          uploadedScript={script.uploaded_script}
          onClose={() => setImportOpen(false)}
          onApply={applyImport}
        />
      )}

      {viewerOpen && (
        <ScriptViewer projectId={project.id} filename={script.uploaded_script?.filename} onClose={() => setViewerOpen(false)} />
      )}

      {chatTarget && (
        <AiChatPanel
          projectId={project.id}
          sectionLabel={`Guion · ${chatTarget.label}`}
          value={chatTarget.value || ""}
          onApply={chatTarget.onApply}
          onClose={() => setChatTarget(null)}
        />
      )}

      <ProjectDocuments projectId={project.id} />
    </section>
  );
}

function ImportModal({ projectId, defaultFormat, uploadedScript, onClose, onApply }) {
  const [text, setText] = useState("");
  const [fmt, setFmt] = useState(defaultFormat || "treatment");
  const [mode, setMode] = useState("append");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState(uploadedScript?.filename ? "uploaded" : "paste");

  const submit = async () => {
    if (source === "paste" && (!text.trim() || text.trim().length < 30)) {
      setError("Pega al menos un párrafo para distribuir.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const body = source === "uploaded"
        ? { use_uploaded: true, target_format: fmt }
        : { text, target_format: fmt };
      const r = await api.post(`/icaa/projects/${projectId}/ai/script/distribute`, body);
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("No se obtuvo job_id");
      // Poll every 4s, up to 8 minutes
      const startedAt = Date.now();
      const MAX_MS = 8 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 4000));
        const p = await api.get(`/icaa/ai/jobs/${jobId}`);
        if (p.data.status === "done") { onApply(p.data.result, mode); return; }
        if (p.data.status === "error") {
          const err = p.data.error || "Error desconocido";
          if (err.toLowerCase().includes("budget")) {
            throw new Error("Se ha agotado el saldo de la IA. Pídele a Eduardo que recargue desde su perfil de Emergent (Universal Key → Add Balance).");
          }
          throw new Error(err);
        }
        if (Date.now() - startedAt > MAX_MS) throw new Error("La IA tardó más de 8 min. Inténtalo con un texto más corto.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error al procesar el texto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2B29]/70 p-4 backdrop-blur"
         onClick={onClose} data-testid="script-import-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white p-7 shadow-2xl">
        <p className="text-[10px] font-semibold uppercase tracking-[0.4em] text-[#26775E]">grabarte · IA</p>
        <h2 className="mt-2 font-display text-2xl">Importar texto y distribuir en secuencias</h2>
        <p className="mt-1 text-sm text-[#2C2B29]/65">
          {uploadedScript?.filename
            ? <>Tienes un guion subido. Puedes usar su texto directamente o pegar otro distinto.</>
            : <>Pega un tratamiento, una escaleta o cualquier texto narrativo. La IA lo leerá y lo organizará respetando tu voz.</>}
        </p>

        {uploadedScript?.filename && (
          <div className="mt-4 inline-flex rounded-full border border-stone-300 bg-white p-1 text-[10px] uppercase tracking-[0.22em]" data-testid="import-source">
            <button onClick={() => setSource("uploaded")} data-testid="import-source-uploaded"
                    className={`rounded-full px-4 py-1.5 ${source === "uploaded" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600"}`}>
              Usar guion subido ({uploadedScript.filename})
            </button>
            <button onClick={() => setSource("paste")} data-testid="import-source-paste"
                    className={`rounded-full px-4 py-1.5 ${source === "paste" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600"}`}>
              Pegar otro texto
            </button>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Formato destino">
            <select value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="import-fmt"
                    className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm">
              <option value="treatment">Tratamiento (bloques + secuencias)</option>
              <option value="scaleta">Escaleta (lista de escenas)</option>
              <option value="screenplay">Guion técnico</option>
            </select>
          </Field>
          <Field label="Modo">
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="import-mode"
                    className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm">
              <option value="append">Añadir al final</option>
              <option value="replace">Reemplazar contenido actual</option>
            </select>
          </Field>
        </div>

        {source === "uploaded" ? (
          <div className="mt-4 rounded-md border border-[#26775E]/30 bg-[#FBFAF4] p-4 text-sm" data-testid="import-uploaded-info">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#26775E]">Fuente: guion subido</p>
            <p className="mt-1 text-stone-800"><strong>{uploadedScript.filename}</strong> · {(uploadedScript.char_count || 0).toLocaleString()} caracteres</p>
            <p className="mt-2 text-[12px] italic text-stone-600">
              La IA leerá el texto completo del guion subido y lo organizará en el formato destino. Tardará 1-2 minutos.
            </p>
          </div>
        ) : (
          <textarea value={text} onChange={(e) => setText(e.target.value)}
                    data-testid="import-text"
                    rows={10} placeholder="Pega aquí el tratamiento o el texto narrativo completo…"
                    className="mt-4 w-full flex-1 resize-y rounded-md border border-stone-300 bg-[#FBFAF4] p-3 font-serif text-[14px] leading-relaxed outline-none focus:border-[#26775E]" />
        )}

        {error && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="import-error">{error}</p>}

        <div className="mt-5 flex items-center justify-between gap-3">
          <p className="text-xs text-stone-500">{source === "uploaded" ? `${(uploadedScript?.char_count || 0).toLocaleString()} caracteres (del guion subido)` : `${text.length.toLocaleString()} caracteres`}</p>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={loading}
                    className="rounded-full border border-[#2C2B29]/20 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2C2B29]/65 hover:bg-[#FAFAF6] disabled:opacity-40">
              Cancelar
            </button>
            <button onClick={submit} disabled={loading} data-testid="import-submit"
                    className="rounded-full bg-[#26775E] px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-[#1c5c48] disabled:opacity-50">
              {loading ? "Distribuyendo…" : "Distribuir con IA"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="col-span-2 block sm:col-span-2">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#2C2B29]/55">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Pill = ({ label, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${
            active ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-600 hover:bg-stone-200"
          }`}>{label}</button>
);

const GenButton = ({ label, subtitle, busy, disabled, onClick, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
          className={`group relative flex items-start gap-3 rounded-xl border bg-white p-3 text-left transition ${
            busy ? "border-[#26775E] bg-[#26775E]/8" : "border-stone-200 hover:border-[#26775E] hover:bg-[#26775E]/5"
          } disabled:cursor-not-allowed disabled:opacity-40`}>
    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${busy ? "bg-[#26775E]" : "bg-stone-900 group-hover:bg-[#26775E]"} text-white`}>
      {busy ? <span className="animate-pulse text-xs">⏳</span> : <span className="text-sm">✨</span>}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[12px] font-semibold uppercase tracking-widest text-stone-900">{busy ? "Generando…" : label}</p>
      <p className="mt-0.5 text-[11px] text-stone-500">{subtitle}</p>
    </div>
  </button>
);
